import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { COMPANY_NAME, SITE_DOMAIN, SITE_NAME, SITE_URL } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function FulfillmentPolicyPage() {
  return (
    <PolicyLayout title="Fulfillment Policy" lastUpdated={UPDATED}>
      <PolicySection title="1. Purpose">
        <p>
          This Fulfillment Policy describes how {COMPANY_NAME} fulfills travel bookings made on {SITE_NAME} ({SITE_URL},
          &ldquo;{SITE_DOMAIN}&rdquo;) — from the moment you submit payment through delivery of confirmations and
          post-booking support.
        </p>
      </PolicySection>

      <PolicySection title="2. Booking fulfillment process">
        <PolicyList
          items={[
            'You search and select travel options displayed from supplier systems and partner networks.',
            'At checkout you provide traveler details, contact information, and payment authorization.',
            'We transmit your request to the applicable airline, hotel, car company, cruise line, or package provider.',
            'Upon supplier acceptance, you receive a confirmation email with itinerary references, tickets, or vouchers.',
          ]}
        />
      </PolicySection>

      <PolicySection title="3. Confirmation and delivery">
        <PolicySubsection title="3.1 Electronic delivery">
          <p>
            Confirmations are delivered by email to the address you provide. You are responsible for ensuring the email
            is correct and for checking spam folders. E-tickets and hotel vouchers may also be available in My Bookings.
          </p>
        </PolicySubsection>
        <PolicySubsection title="3.2 Timing">
          <p>
            Most flight and hotel confirmations arrive within minutes. Complex itineraries, group bookings, or manual
            fare verification may take up to twenty-four (24) hours. If you do not receive confirmation within that window,
            contact us immediately.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="4. Pending and unconfirmed bookings">
        <p>
          Occasionally a fare is no longer available after payment authorization. If we cannot secure the itinerary at
          the quoted price, we will notify you and offer alternatives or a full refund of amounts charged for the
          unconfirmed segment. We do not guarantee fulfillment until you receive a valid supplier confirmation.
        </p>
      </PolicySection>

      <PolicySection title="5. Ticketing and travel documents">
        <PolicyList
          items={[
            'Airline e-ticket numbers are issued per passenger and must match government ID at check-in.',
            'Hotel confirmations include property address, check-in/out times, and rate rules.',
            'Car rentals require a valid driver license and credit card at pickup per supplier policy.',
            'Cruise bookings may require online check-in and passport information before sailing.',
          ]}
        />
      </PolicySection>

      <PolicySection title="6. Third-party fulfillment">
        <p>
          Travel services are fulfilled by independent suppliers. {SITE_NAME} coordinates booking and payment but does not
          operate aircraft, hotels, or vehicles. Service quality, schedules, and on-site experience are the supplier&apos;s
          responsibility subject to their terms.
        </p>
      </PolicySection>

      <PolicySection title="7. Errors and duplicate bookings">
        <p>
          If you believe a booking was made in error or duplicated, notify us within two (2) hours of confirmation for
          the best chance of voiding without penalty, subject to airline and supplier rules. After that window, standard
          change and cancellation policies apply.
        </p>
      </PolicySection>

      <PolicySection title="8. Chargebacks and payment holds">
        <p>
          Initiating a chargeback before contacting us may result in cancellation of associated travel and suspension of
          account access while the dispute is investigated. We cooperate with card networks and provide booking records
          as required.
        </p>
      </PolicySection>

      <PolicySection title="9. Related policies">
        <p>
          <Link to="/flight/cancellation-refund">Cancellation &amp; Refund</Link> ·{' '}
          <Link to="/flight/service-fees">Service Fees</Link> · <Link to="/flight/terms">Terms &amp; Conditions</Link>
        </p>
      </PolicySection>

      <PolicySection title="10. Contact us">
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
